import { supabase } from '@/utils/supabase';
import { getCurrentUser } from '@/utils/auth';
import { parseAsLocalDate } from '@/utils/dateHelpers';

export interface PartnerContact {
  id: string;
  organizationId: string;
  
  // Basic contact information
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  mobilePhone?: string;
  workPhone?: string;
  
  // Business information
  companyName: string;
  companyType?: 'brokerage' | 'bank' | 'credit_union' | 'independent' | 'other';
  title?: string;
  licenseNumber?: string;
  licenseState?: string;
  licenseExpiry?: Date;
  website?: string;
  
  // Partner classification
  partnerType: 'realtor' | 'financial_advisor' | 'insurance_agent' | 'attorney' | 'cpa' | 'other';
  partnerStatus?: 'active' | 'inactive' | 'pending' | 'suspended';
  partnerTier?: 'bronze' | 'silver' | 'gold' | 'platinum';
  
  // Business address
  businessStreet?: string;
  businessUnit?: string;
  businessCity?: string;
  businessState?: string;
  businessZip?: string;
  businessCountry?: string;
  
  // Referral agreement details
  referralAgreementSigned?: boolean;
  referralAgreementDate?: Date;
  referralFeeType?: 'percentage' | 'flat_fee' | 'tiered' | 'none';
  referralFeePercentage?: number;
  referralFeeFlatAmount?: number;
  
  // Performance metrics
  totalReferrals?: number;
  activeReferrals?: number;
  closedReferrals?: number;
  totalVolume?: number;
  lifetimeCommission?: number;
  averageLoanAmount?: number;
  conversionRate?: number;
  lastReferralDate?: Date;
  
  // Communication preferences
  preferredContactMethod?: 'email' | 'phone' | 'text' | 'mail';
  communicationFrequency?: 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'as_needed';
  emailOptIn?: boolean;
  smsOptIn?: boolean;
  
  // Internal notes and metadata
  notes?: string;
  tags?: string[];
  customFields?: Record<string, unknown>;
  source?: string;
  sourceDetails?: string;
  
  // Important dates
  partnerSince?: Date;
  lastContactDate?: Date;
  nextScheduledContact?: Date;
  birthday?: Date;
  
  // Assignment and ownership
  assignedOfficerId?: string;
  createdBy?: string;
  updatedBy?: string;
  
  // Timestamps
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
}

export interface CreatePartnerContactInput {
  firstName: string;
  lastName: string;
  companyName: string;
  partnerType: PartnerContact['partnerType'];
  email?: string;
  phone?: string;
  mobilePhone?: string;
  workPhone?: string;
  companyType?: PartnerContact['companyType'];
  title?: string;
  licenseNumber?: string;
  licenseState?: string;
  website?: string;
  businessStreet?: string;
  businessUnit?: string;
  businessCity?: string;
  businessState?: string;
  businessZip?: string;
  preferredContactMethod?: PartnerContact['preferredContactMethod'];
  notes?: string;
  tags?: string[];
  source?: string;
  sourceDetails?: string;
}

export interface UpdatePartnerContactInput extends Partial<CreatePartnerContactInput> {
  partnerStatus?: PartnerContact['partnerStatus'];
  referralAgreementSigned?: boolean;
  referralAgreementDate?: Date;
  referralFeeType?: PartnerContact['referralFeeType'];
  referralFeePercentage?: number;
  referralFeeFlatAmount?: number;
  communicationFrequency?: PartnerContact['communicationFrequency'];
  emailOptIn?: boolean;
  smsOptIn?: boolean;
  nextScheduledContact?: Date;
  birthday?: Date;
  assignedOfficerId?: string;
  customFields?: Record<string, unknown>;
}

export interface PartnerContactFilters {
  search?: string;
  partnerType?: PartnerContact['partnerType'];
  partnerStatus?: PartnerContact['partnerStatus'];
  partnerTier?: PartnerContact['partnerTier'];
  assignedOfficerId?: string;
  tags?: string[];
  hasReferralAgreement?: boolean;
  activityLevel?: 'very_active' | 'active' | 'moderate' | 'inactive';
}

class PartnerContactService {
  /**
   * Get the current user's organization ID
   */
  private async getUserOrganizationId(): Promise<string> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data: profile, error } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', user.id)
      .single();

    if (error || !profile?.organization_id) {
      throw new Error('Organization not found for user');
    }

    return profile.organization_id;
  }

  /**
   * Check if a partner email already exists in the organization
   */
  async checkEmailExists(email: string | undefined, excludeId?: string): Promise<PartnerContact | null> {
    if (!email) {
return null;
}

    const organizationId = await this.getUserOrganizationId();

    let query = supabase
      .from('partner_contacts')
      .select('*')
      .eq('organization_id', organizationId)
      .ilike('email', email)
      .is('deleted_at', null);

    if (excludeId) {
      query = query.neq('id', excludeId);
    }

    const { data, error } = await query.maybeSingle();

    if (error) {
      console.error('Error checking partner email existence:', error);
      return null;
    }

    return data ? this.mapToPartnerContact(data) : null;
  }

  /**
   * Check if a partner phone already exists in the organization
   * Normalizes phone to last 10 digits for comparison
   */
  async checkPhoneExists(phone: string | undefined, excludeId?: string): Promise<PartnerContact | null> {
    if (!phone) {
return null;
}

    const normalizedPhone = phone.replace(/\D/g, '').slice(-10);
    if (normalizedPhone.length < 10) {
      return null;
    }

    const organizationId = await this.getUserOrganizationId();

    const { data, error } = await supabase
      .from('partner_contacts')
      .select('*')
      .eq('organization_id', organizationId)
      .not('mobile_phone', 'is', null)
      .is('deleted_at', null);

    if (error || !data) {
      console.error('Error checking partner phone existence:', error);
      return null;
    }

    const match = data.find(partner => {
      if (!partner.mobile_phone) {
return false;
}
      if (excludeId && partner.id === excludeId) {
return false;
}
      const partnerPhone = partner.mobile_phone.replace(/\D/g, '').slice(-10);
      return partnerPhone === normalizedPhone;
    });

    return match ? this.mapToPartnerContact(match) : null;
  }

  /**
   * Create a new partner contact
   */
  async create(input: CreatePartnerContactInput): Promise<PartnerContact> {
    const user = await getCurrentUser();
    if (!user?.organizationId) {
      throw new Error('User must belong to an organization');
    }

    const { data, error } = await supabase
      .from('partner_contacts')
      .insert({
        organization_id: user.organizationId,
        first_name: input.firstName,
        last_name: input.lastName,
        company_name: input.companyName,
        partner_type: input.partnerType,
        email: input.email,
        phone: input.phone,
        mobile_phone: input.mobilePhone,
        work_phone: input.workPhone,
        company_type: input.companyType,
        title: input.title,
        license_number: input.licenseNumber,
        license_state: input.licenseState,
        website: input.website,
        business_street: input.businessStreet,
        business_unit: input.businessUnit,
        business_city: input.businessCity,
        business_state: input.businessState,
        business_zip: input.businessZip,
        preferred_contact_method: input.preferredContactMethod,
        notes: input.notes,
        tags: input.tags,
        source: input.source,
        source_details: input.sourceDetails,
        created_by: user.id,
        assigned_officer_id: user.id // Default to creator
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating partner contact:', error);
      throw new Error(error.message);
    }

    return this.mapToPartnerContact(data);
  }

  /**
   * Get a partner contact by ID
   */
  async getById(id: string): Promise<PartnerContact | null> {
    const organizationId = await this.getUserOrganizationId();

    const { data, error } = await supabase
      .from('partner_contacts')
      .select('*')
      .eq('id', id)
      .eq('organization_id', organizationId)
      .is('deleted_at', null)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
return null;
} // Not found
      console.error('Error fetching partner contact:', error);
      throw new Error(error.message);
    }

    return this.mapToPartnerContact(data);
  }

  /**
   * List partner contacts with optional filters
   */
  async list(filters?: PartnerContactFilters): Promise<PartnerContact[]> {
    const organizationId = await this.getUserOrganizationId();

    let query = supabase
      .from('partner_contacts')
      .select('*')
      .eq('organization_id', organizationId)
      .is('deleted_at', null)
      .order('company_name', { ascending: true });

    // Apply filters
    if (filters?.search) {
      query = query.or(
        `first_name.ilike.%${filters.search}%,` +
        `last_name.ilike.%${filters.search}%,` +
        `email.ilike.%${filters.search}%,` +
        `company_name.ilike.%${filters.search}%`
      );
    }

    if (filters?.partnerType) {
      query = query.eq('partner_type', filters.partnerType);
    }

    if (filters?.partnerStatus) {
      query = query.eq('partner_status', filters.partnerStatus);
    }

    if (filters?.partnerTier) {
      query = query.eq('partner_tier', filters.partnerTier);
    }

    if (filters?.assignedOfficerId) {
      query = query.eq('assigned_officer_id', filters.assignedOfficerId);
    }

    if (filters?.tags?.length) {
      query = query.contains('tags', filters.tags);
    }

    if (filters?.hasReferralAgreement !== undefined) {
      query = query.eq('referral_agreement_signed', filters.hasReferralAgreement);
    }

    if (filters?.activityLevel) {
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const ninetyDaysAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      const halfYearAgo = new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000);

      switch (filters.activityLevel) {
        case 'very_active':
          query = query.gte('last_referral_date', thirtyDaysAgo.toISOString());
          break;
        case 'active':
          query = query.gte('last_referral_date', ninetyDaysAgo.toISOString());
          break;
        case 'moderate':
          query = query.gte('last_referral_date', halfYearAgo.toISOString());
          break;
        case 'inactive':
          query = query.or(
            `last_referral_date.lt.${halfYearAgo.toISOString()},last_referral_date.is.null`
          );
          break;
      }
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error listing partner contacts:', error);
      throw new Error(error.message);
    }

    return data.map(this.mapToPartnerContact);
  }

  /**
   * Update a partner contact
   */
  async update(id: string, input: UpdatePartnerContactInput): Promise<PartnerContact> {
    const user = await getCurrentUser();
    if (!user) {
      throw new Error('User must be authenticated');
    }

    const organizationId = await this.getUserOrganizationId();

    const updateData: any = {
      updated_by: user.id,
      updated_at: new Date().toISOString()
    };

    // Map input fields to database columns
    if (input.firstName !== undefined) {
updateData.first_name = input.firstName;
}
    if (input.lastName !== undefined) {
updateData.last_name = input.lastName;
}
    if (input.email !== undefined) {
updateData.email = input.email;
}
    if (input.phone !== undefined) {
updateData.phone = input.phone;
}
    if (input.mobilePhone !== undefined) {
updateData.mobile_phone = input.mobilePhone;
}
    if (input.workPhone !== undefined) {
updateData.work_phone = input.workPhone;
}
    if (input.companyName !== undefined) {
updateData.company_name = input.companyName;
}
    if (input.companyType !== undefined) {
updateData.company_type = input.companyType;
}
    if (input.title !== undefined) {
updateData.title = input.title;
}
    if (input.licenseNumber !== undefined) {
updateData.license_number = input.licenseNumber;
}
    if (input.licenseState !== undefined) {
updateData.license_state = input.licenseState;
}
    if (input.website !== undefined) {
updateData.website = input.website;
}
    if (input.businessStreet !== undefined) {
updateData.business_street = input.businessStreet;
}
    if (input.businessUnit !== undefined) {
updateData.business_unit = input.businessUnit;
}
    if (input.businessCity !== undefined) {
updateData.business_city = input.businessCity;
}
    if (input.businessState !== undefined) {
updateData.business_state = input.businessState;
}
    if (input.businessZip !== undefined) {
updateData.business_zip = input.businessZip;
}
    if (input.partnerType !== undefined) {
updateData.partner_type = input.partnerType;
}
    if (input.partnerStatus !== undefined) {
updateData.partner_status = input.partnerStatus;
}
    if (input.preferredContactMethod !== undefined) {
updateData.preferred_contact_method = input.preferredContactMethod;
}
    if (input.communicationFrequency !== undefined) {
updateData.communication_frequency = input.communicationFrequency;
}
    if (input.emailOptIn !== undefined) {
updateData.email_opt_in = input.emailOptIn;
}
    if (input.smsOptIn !== undefined) {
updateData.sms_opt_in = input.smsOptIn;
}
    if (input.notes !== undefined) {
updateData.notes = input.notes;
}
    if (input.tags !== undefined) {
updateData.tags = input.tags;
}
    if (input.source !== undefined) {
updateData.source = input.source;
}
    if (input.sourceDetails !== undefined) {
updateData.source_details = input.sourceDetails;
}
    if (input.referralAgreementSigned !== undefined) {
updateData.referral_agreement_signed = input.referralAgreementSigned;
}
    if (input.referralAgreementDate !== undefined) {
updateData.referral_agreement_date = input.referralAgreementDate;
}
    if (input.referralFeeType !== undefined) {
updateData.referral_fee_type = input.referralFeeType;
}
    if (input.referralFeePercentage !== undefined) {
updateData.referral_fee_percentage = input.referralFeePercentage;
}
    if (input.referralFeeFlatAmount !== undefined) {
updateData.referral_fee_flat_amount = input.referralFeeFlatAmount;
}
    if (input.nextScheduledContact !== undefined) {
updateData.next_scheduled_contact = input.nextScheduledContact;
}
    if (input.birthday !== undefined) {
updateData.birthday = input.birthday;
}
    if (input.assignedOfficerId !== undefined) {
updateData.assigned_officer_id = input.assignedOfficerId;
}
    if (input.customFields !== undefined) {
updateData.custom_fields = input.customFields;
}

    const { data, error } = await supabase
      .from('partner_contacts')
      .update(updateData)
      .eq('id', id)
      .eq('organization_id', organizationId)
      .is('deleted_at', null)
      .select()
      .single();

    if (error) {
      console.error('Error updating partner contact:', error);
      throw new Error(error.message);
    }

    return this.mapToPartnerContact(data);
  }

  /**
   * Soft delete a partner contact
   */
  async delete(id: string): Promise<void> {
    const user = await getCurrentUser();
    if (!user) {
      throw new Error('User must be authenticated');
    }

    const organizationId = await this.getUserOrganizationId();

    const { error } = await supabase
      .from('partner_contacts')
      .update({
        deleted_at: new Date().toISOString(),
        updated_by: user.id,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .eq('organization_id', organizationId)
      .is('deleted_at', null);

    if (error) {
      console.error('Error deleting partner contact:', error);
      throw new Error(error.message);
    }
  }

  /**
   * Get partner performance summary
   */
  async getPerformanceSummary(): Promise<{
    totalPartners: number;
    activePartners: number;
    totalReferrals: number;
    totalVolume: number;
    averageConversionRate: number;
  }> {
    const organizationId = await this.getUserOrganizationId();

    const { data, error } = await supabase
      .from('partner_contacts')
      .select('partner_status, total_referrals, total_volume, conversion_rate')
      .eq('organization_id', organizationId)
      .is('deleted_at', null);

    if (error) {
      console.error('Error fetching performance summary:', error);
      throw new Error(error.message);
    }

    const totalPartners = data.length;
    const activePartners = data.filter(p => p.partner_status === 'active').length;
    const totalReferrals = data.reduce((sum, p) => sum + (p.total_referrals || 0), 0);
    const totalVolume = data.reduce((sum, p) => sum + (p.total_volume || 0), 0);
    
    const partnersWithConversion = data.filter(p => p.conversion_rate > 0);
    const averageConversionRate = partnersWithConversion.length > 0
      ? partnersWithConversion.reduce((sum, p) => sum + p.conversion_rate, 0) / partnersWithConversion.length
      : 0;

    return {
      totalPartners,
      activePartners,
      totalReferrals,
      totalVolume,
      averageConversionRate
    };
  }

  /**
   * Map database record to PartnerContact type
   */
  private mapToPartnerContact(data: unknown): PartnerContact {
    return {
      id: data.id,
      organizationId: data.organization_id,
      firstName: data.first_name,
      lastName: data.last_name,
      email: data.email,
      phone: data.phone,
      mobilePhone: data.mobile_phone,
      workPhone: data.work_phone,
      companyName: data.company_name,
      companyType: data.company_type,
      title: data.title,
      licenseNumber: data.license_number,
      licenseState: data.license_state,
      licenseExpiry: data.license_expiry ? parseAsLocalDate(data.license_expiry) : undefined,
      website: data.website,
      partnerType: data.partner_type,
      partnerStatus: data.partner_status,
      partnerTier: data.partner_tier,
      businessStreet: data.business_street,
      businessUnit: data.business_unit,
      businessCity: data.business_city,
      businessState: data.business_state,
      businessZip: data.business_zip,
      businessCountry: data.business_country,
      referralAgreementSigned: data.referral_agreement_signed,
      referralAgreementDate: data.referral_agreement_date ? parseAsLocalDate(data.referral_agreement_date) : undefined,
      referralFeeType: data.referral_fee_type,
      referralFeePercentage: data.referral_fee_percentage,
      referralFeeFlatAmount: data.referral_fee_flat_amount,
      totalReferrals: data.total_referrals,
      activeReferrals: data.active_referrals,
      closedReferrals: data.closed_referrals,
      totalVolume: data.total_volume,
      lifetimeCommission: data.lifetime_commission,
      averageLoanAmount: data.average_loan_amount,
      conversionRate: data.conversion_rate,
      lastReferralDate: data.last_referral_date ? parseAsLocalDate(data.last_referral_date) : undefined,
      preferredContactMethod: data.preferred_contact_method,
      communicationFrequency: data.communication_frequency,
      emailOptIn: data.email_opt_in,
      smsOptIn: data.sms_opt_in,
      notes: data.notes,
      tags: data.tags,
      customFields: data.custom_fields,
      source: data.source,
      sourceDetails: data.source_details,
      partnerSince: data.partner_since ? parseAsLocalDate(data.partner_since) : undefined,
      lastContactDate: data.last_contact_date ? parseAsLocalDate(data.last_contact_date) : undefined,
      nextScheduledContact: data.next_scheduled_contact ? parseAsLocalDate(data.next_scheduled_contact) : undefined,
      birthday: data.birthday ? parseAsLocalDate(data.birthday) : undefined,
      assignedOfficerId: data.assigned_officer_id,
      createdBy: data.created_by,
      updatedBy: data.updated_by,
      createdAt: parseAsLocalDate(data.created_at),
      updatedAt: parseAsLocalDate(data.updated_at),
      deletedAt: data.deleted_at ? parseAsLocalDate(data.deleted_at) : undefined
    };
  }
}

export const partnerContactService = new PartnerContactService();